import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaSearchPlus, FaSpinner, FaTimes, FaChevronRight, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { CreditAnalysis, CcirProperty, CcirOwner, CcirGroupMetrics, CcirDetailsResponse } from './types';
import { fmtCurrency } from './utils/currencyUtils';
import {
    calcTotalArea,
    calcTotalValue,
    calcDraweePatrimony,
    getConsolidatedOwners,
    fmtParticipation,
    getOwnerColor,
    calcGroupMetrics,
    getOwnerErrorDetails,
    getOwnerBureauStatus,
    hasGroupError,
    OwnerBureauStatus,
} from './utils/ccirUtils';
import { maskDocument, unmaskDocument } from './utils/documentUtils';
import { getCcirDetails, requestDeepAnalysis, retryCcirBureau } from '../../../services/CreditAnalysisService';

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface CcirModalProps {
    isOpen: boolean;
    onClose: () => void;
    row: CreditAnalysis;
    onDeepAnalysisComplete?: (itemId: string, metrics: CcirGroupMetrics) => void;
}

interface NavigationEntry {
    document: string;
    name: string;
    data: CcirDetailsResponse;
}

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const parseNumeric = (value: string | undefined): number | undefined => {
    if (!value) return undefined;
    const parsed = Number.parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
};

const enrichDraweeFromRow = (response: CcirDetailsResponse, row: CreditAnalysis): CcirDetailsResponse => {
    const scrTotal = parseNumeric(row.scrTotal);
    const scrVencido = parseNumeric(row.overdue);
    const scrPrejuizo = parseNumeric(row.scrPrejuizo);
    const pdefault = parseNumeric(row.pdefAgro);
    const agroScore = parseNumeric(row.agroScore);
    const patrimony = calcDraweePatrimony(response.properties);
    const patrimonyDebtRatio = scrTotal && scrTotal > 0 && patrimony ? patrimony / scrTotal : undefined;

    const coOwners = response.coOwners.map(owner => {
        if (!owner.isDrawee) return owner;
        return { ...owner, scrTotal, scrVencido, scrPrejuizo, pdefault, agroScore, patrimony, patrimonyDebtRatio };
    });

    return { ...response, coOwners };
};

const applyDraweeMetrics = (data: CcirDetailsResponse, metrics?: CcirOwner): CcirDetailsResponse => {
    if (!metrics) return data;
    return {
        ...data,
        coOwners: data.coOwners.map(co => {
            if (!co.isDrawee) return co;
            return {
                ...co,
                scrTotal: co.scrTotal ?? metrics.scrTotal,
                scrVencido: co.scrVencido ?? metrics.scrVencido,
                scrPrejuizo: co.scrPrejuizo ?? metrics.scrPrejuizo,
                pdefault: co.pdefault ?? metrics.pdefault,
                agroScore: co.agroScore ?? metrics.agroScore,
                patrimony: co.patrimony ?? metrics.patrimony,
                patrimonyDebtRatio: co.patrimonyDebtRatio ?? metrics.patrimonyDebtRatio,
            };
        }),
    };
};

const enrichCoOwnersFromBureauData = (coOwners: CcirOwner[], properties: CcirProperty[]): CcirOwner[] =>
    coOwners.map(owner => {
        if (owner.isDrawee || !owner.bureauData) return owner;

        const scr = owner.bureauData.scr;
        const acriscore = owner.bureauData.acriscore;
        const serasa = owner.bureauData.serasa;
        const ccir = owner.bureauData.ccir;

        const scrTotal = parseNumeric(scr?.creditoTotal);
        const scrVencido = parseNumeric(scr?.creditoVencido);
        const scrPrejuizo = parseNumeric(scr?.creditoPrejuizo);
        const pdefault = parseNumeric(acriscore?.defaultRate) ?? parseNumeric(serasa?.agroscorePdef);
        const agroScore = acriscore?.score ?? serasa?.agroscore ?? undefined;
        const patrimony = ccir ? ccir.totalValue * (owner.participationPercent / 100) : undefined;
        const patrimonyDebtRatio = scrTotal && scrTotal > 0 && patrimony ? patrimony / scrTotal : undefined;

        return { ...owner, scrTotal, scrVencido, scrPrejuizo, pdefault, agroScore, patrimony, patrimonyDebtRatio };
    });

const buildPieSlices = (owners: CcirOwner[]): { owner: CcirOwner; startAngle: number; endAngle: number; color: string }[] => {
    const sorted = [...owners].sort((a, b) => {
        if (a.isDrawee) return -1;
        if (b.isDrawee) return 1;
        return b.participationPercent - a.participationPercent;
    });

    let currentAngle = 0;
    return sorted.map((owner, i) => {
        const angle = (owner.participationPercent / 100) * 360;
        const slice = {
            owner,
            startAngle: currentAngle,
            endAngle: currentAngle + angle,
            color: getOwnerColor(i, owner.isDrawee),
        };
        currentAngle += angle;
        return slice;
    });
};

const describeArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number): string => {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
};

const polarToCartesian = (cx: number, cy: number, r: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(angleInRadians), y: cy + r * Math.sin(angleInRadians) };
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface PieChartProps {
    owners: CcirOwner[];
}

function PieChart({ owners }: PieChartProps) {
    const slices = useMemo(() => buildPieSlices(owners), [owners]);
    const size = 200;
    const cx = size / 2;
    const cy = size / 2;
    const r = 80;

    if (owners.length === 0) {
        return <div className="text-gray-400 dark:text-gray-500 text-sm text-center py-8">Sem dados de proprietários</div>;
    }

    if (owners.length === 1) {
        return (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={cx} cy={cy} r={r} fill={getOwnerColor(0, owners[0].isDrawee)} />
            </svg>
        );
    }

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {slices.map((slice, i) => (
                <path
                    key={i}
                    d={describeArc(cx, cy, r, slice.startAngle, slice.endAngle)}
                    fill={slice.color}
                    stroke="white"
                    strokeWidth="1"
                    className="dark:stroke-slate-800"
                />
            ))}
        </svg>
    );
}

interface ChartLegendProps {
    owners: CcirOwner[];
    draweeDocument?: string;
}

function ChartLegend({ owners, draweeDocument }: ChartLegendProps) {
    const sorted = [...owners].sort((a, b) => {
        if (a.isDrawee) return -1;
        if (b.isDrawee) return 1;
        return b.participationPercent - a.participationPercent;
    });

    return (
        <div className="space-y-1">
            {sorted.map((owner, i) => {
                const isDraweeLabel = draweeDocument && unmaskDocument(owner.document) === unmaskDocument(draweeDocument);
                return (
                <div key={owner.document} className="flex items-center gap-1.5 text-xs">
                    <div
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: getOwnerColor(i, owner.isDrawee) }}
                    />
                    <span
                        className={`flex-1 min-w-0 truncate ${owner.isDrawee ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}
                        title={owner.name}
                    >
                        {owner.name}
                        {isDraweeLabel && <span className="text-[10px] text-gray-400 ml-0.5">(sacado)</span>}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100 font-medium ml-auto shrink-0">
                        {fmtParticipation(owner.participationPercent)}
                    </span>
                </div>
                );
            })}
        </div>
    );
}

interface SummaryCardProps {
    label: string;
    value: string;
    muted?: boolean;
}

function SummaryCard({ label, value, muted }: SummaryCardProps) {
    return (
        <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
            <div className={`text-lg font-semibold mt-0.5 ${muted ? 'text-gray-400 dark:text-gray-500 italic text-sm' : 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
        </div>
    );
}

interface BureauErrorDetailModalProps {
    owner: CcirOwner | null;
    onClose: () => void;
}

function BureauErrorDetailModal({ owner, onClose }: BureauErrorDetailModalProps) {
    if (!owner) return null;
    const details = getOwnerErrorDetails(owner);
    const titleId = `bureau-error-title-${owner.document}`;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="border-b border-gray-200 dark:border-slate-600 px-5 py-3 flex items-center justify-between">
                    <div className="min-w-0">
                        <h3 id={titleId} className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate" title={owner.name}>{owner.name}</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Detalhes das consultas</span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 shrink-0" aria-label="Fechar">
                        <FaTimes />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-3">
                    {details.map((d, i) => (
                        <div key={`${d.bureau}-${i}`} className="flex items-start gap-2 text-sm">
                            {d.cause === 'ERROR'
                                ? <FaExclamationTriangle className="shrink-0 mt-0.5 text-red-500 dark:text-red-400" />
                                : <FaInfoCircle className="shrink-0 mt-0.5 text-amber-500 dark:text-amber-400" />}
                            <div className="min-w-0">
                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {d.bureau}
                                    <span className={`ml-2 text-xs font-normal ${d.cause === 'ERROR' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                        {d.cause === 'ERROR' ? 'Falha na consulta' : 'Sem registro'}
                                    </span>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                                    {d.message || (d.cause === 'ERROR' ? 'Não foi possível consultar este bureau.' : 'Nenhum registro encontrado neste bureau.')}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/** Unique list of bureaus with real ERROR (not NOT_FOUND) across all co-owners. */
const getFailedBureaus = (coOwners: CcirOwner[]): string[] =>
    Array.from(new Set(
        coOwners.flatMap(co =>
            getOwnerErrorDetails(co)
                .filter(d => d.cause === OwnerBureauStatus.ERROR)
                .map(d => d.bureau)
        )
    ));

interface BureauErrorBannerProps {
    failedBureaus: string[];
    retrying: boolean;
    onRetry: () => void;
}

function BureauErrorBanner({ failedBureaus, retrying, onRetry }: BureauErrorBannerProps) {
    if (failedBureaus.length === 0) return null;
    return (
        <div className="mb-3 flex items-center justify-between gap-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <FaExclamationTriangle className="shrink-0" />
                <span>Consultas com falha: <strong>{failedBureaus.join(', ')}</strong> — dados incompletos para alguns coproprietários. As métricas de grupo não são calculadas até o reprocessamento.</span>
            </div>
            <button
                onClick={onRetry}
                disabled={retrying}
                className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 dark:bg-red-500 text-white rounded hover:bg-red-700 dark:hover:bg-red-400 disabled:opacity-50 transition-colors"
            >
                {retrying ? <FaSpinner className="animate-spin" /> : null}
                Retentar
            </button>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// POLLING HELPER
// ═══════════════════════════════════════════════════════════════

interface PollCallbacks {
    onComplete: (data: CcirDetailsResponse, metrics: CcirGroupMetrics) => void;
    onTimeout: (data: CcirDetailsResponse) => void;
    onError: () => void;
}

const startPollingCcirDetails = (
    itemId: string,
    row: CreditAnalysis | null,
    callbacks: PollCallbacks,
    maxAttempts = 30,
    interval = 3000,
): (() => void) => {
    let attempts = 0;
    let cancelled = false;

    const poll = () => {
        if (cancelled) return;
        attempts++;
        getCcirDetails(itemId)
            .then(response => {
                if (cancelled) return;
                const enriched = row ? enrichDraweeFromRow(response, row) : response;
                if (enriched.deepAnalysisCompleted) {
                    const coOwners = enrichCoOwnersFromBureauData(enriched.coOwners, enriched.properties);
                    const metrics = calcGroupMetrics(coOwners, enriched.properties);
                    callbacks.onComplete({ ...enriched, coOwners, groupMetrics: metrics }, metrics);
                    return;
                }
                if (attempts >= maxAttempts) {
                    callbacks.onTimeout(enriched);
                    return;
                }
                setTimeout(poll, interval);
            })
            .catch(() => { if (!cancelled) callbacks.onError(); });
    };

    setTimeout(poll, interval);
    return () => { cancelled = true; };
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function CcirModal({ isOpen, onClose, row, onDeepAnalysisComplete }: CcirModalProps) {
    const [data, setData] = useState<CcirDetailsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [deepAnalysisLoading, setDeepAnalysisLoading] = useState(false);
    const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
    const [navStack, setNavStack] = useState<NavigationEntry[]>([]);
    const [currentItemId, setCurrentItemId] = useState<string>(row.uuid);
    const [showFormulas, setShowFormulas] = useState(false);
    const [errorDetailOwner, setErrorDetailOwner] = useState<CcirOwner | null>(null);
    const draweeMetricsRef = useRef<CcirOwner | undefined>(undefined);
    const cancelPollingRef = useRef<(() => void) | null>(null);

    const currentName = navStack.length > 0 && data ? data.draweeName : row.drawee;
    const currentDocument = navStack.length > 0 && data ? data.draweeDocument : row.document;

    const startPolling = useCallback(() => {
        const isRoot = currentItemId === row.uuid;
        cancelPollingRef.current?.();
        const cancel = startPollingCcirDetails(currentItemId, isRoot ? row : null, {
            onComplete: (enrichedData, metrics) => {
                // Safety: re-enrich drawee in case polling helper closure was stale
                if (isRoot) {
                    const draweeCoOwners = enrichedData.coOwners.map(co => {
                        if (!co.isDrawee) return co;
                        return {
                            ...co,
                            scrTotal: co.scrTotal ?? parseNumeric(row.scrTotal),
                            scrVencido: co.scrVencido ?? parseNumeric(row.overdue),
                            scrPrejuizo: co.scrPrejuizo ?? parseNumeric(row.scrPrejuizo),
                            pdefault: co.pdefault ?? parseNumeric(row.pdefAgro),
                            agroScore: co.agroScore ?? parseNumeric(row.agroScore),
                            patrimony: co.patrimony ?? calcDraweePatrimony(enrichedData.properties),
                            patrimonyDebtRatio: co.patrimonyDebtRatio ?? (
                                parseNumeric(row.scrTotal) && parseNumeric(row.scrTotal)! > 0
                                    ? calcDraweePatrimony(enrichedData.properties) / parseNumeric(row.scrTotal)!
                                    : undefined
                            ),
                        };
                    });
                    setData({ ...enrichedData, coOwners: draweeCoOwners, groupMetrics: metrics });
                } else {
                    const withDrawee = applyDraweeMetrics(enrichedData, draweeMetricsRef.current);
                    setData(withDrawee);
                }
                setDeepAnalysisLoading(false);
                const remainingErrors = hasGroupError(enrichedData.coOwners);
                if (remainingErrors) {
                    toast.warning('Processamento concluído, mas alguns bureaus continuam com erro');
                } else if (enrichedData.deepAnalysisCompleted) {
                    toast.success('Análise dos coproprietários concluída');
                }
                if (isRoot && onDeepAnalysisComplete) onDeepAnalysisComplete(row.uuid, metrics);
            },
            onTimeout: (enrichedData) => {
                setData(enrichedData);
                setDeepAnalysisLoading(false);
            },
            onError: () => setDeepAnalysisLoading(false),
        });
        cancelPollingRef.current = cancel;
        return cancel;
    }, [currentItemId, row.uuid]);

    useEffect(() => {
        if (!isOpen || !row?.uuid) return;
        setLoading(true);
        setSelectedPropertyId(null);
        setNavStack([]);
        setCurrentItemId(row.uuid);
        draweeMetricsRef.current = undefined;

        let cancelPolling: (() => void) | undefined;

        getCcirDetails(row.uuid)
            .then(response => {
                const enriched = enrichDraweeFromRow(response, row);
                if (enriched.deepAnalysisCompleted) {
                    const coOwnersWithMetrics = enrichCoOwnersFromBureauData(enriched.coOwners, enriched.properties);
                    const metrics = calcGroupMetrics(coOwnersWithMetrics, enriched.properties);
                    setData({ ...enriched, coOwners: coOwnersWithMetrics, groupMetrics: metrics });
                    if (onDeepAnalysisComplete) onDeepAnalysisComplete(row.uuid, metrics);
                } else if (enriched.deepAnalysisBatchId) {
                    setData(enriched);
                    setDeepAnalysisLoading(true);
                    cancelPolling = startPolling();
                } else {
                    setData(enriched);
                }
            })
            .catch(() => setData(null))
            .finally(() => setLoading(false));

        return () => { if (cancelPolling) cancelPolling(); cancelPollingRef.current?.(); cancelPollingRef.current = null; };
    }, [isOpen, row?.uuid]);

    const chartOwners = useMemo(() => {
        if (!data) return [];
        if (!selectedPropertyId) return getConsolidatedOwners(data.properties);
        const byCity = data.properties.filter(p => `${p.city}-${p.state}` === selectedPropertyId);
        if (byCity.length > 0) return getConsolidatedOwners(byCity);
        const byRegion = data.properties.filter(p => p.agroconsultRegion === selectedPropertyId);
        if (byRegion.length > 0) return getConsolidatedOwners(byRegion);
        return getConsolidatedOwners(data.properties);
    }, [data, selectedPropertyId]);

    // Real technical failures among co-owners mean group figures are partial and
    // must not be shown (a NOT_FOUND "no record" outcome does NOT taint the group).
    const groupHasError = useMemo(() => data ? hasGroupError(data.coOwners) : false, [data]);

    const handleDeepAnalysis = async () => {
        if (!data) return;
        setDeepAnalysisLoading(true);

        try {
            await requestDeepAnalysis(currentItemId);
            startPolling();
        } catch (err: any) {
            setDeepAnalysisLoading(false);
            if (err.message?.includes('já')) {
                startPolling();
            }
        }
    };

    const handleRetryFailedBureaus = async (batchId: string, failedBureaus: string[]) => {
        setDeepAnalysisLoading(true);
        let retried = 0;
        for (const bureau of failedBureaus) {
            try {
                await retryCcirBureau(batchId, bureau);
                retried++;
            } catch { /* continue with next bureau */ }
        }
        if (retried > 0) {
            toast.info(`Retentativa disparada para ${retried} bureau(s). Aguardando resultado...`);
            startPolling();
            return;
        }
        toast.error('Falha ao disparar retentativa');
        setDeepAnalysisLoading(false);
    };

    const loadCcirForItem = useCallback((itemId: string, ownerMetrics?: CcirOwner) => {
        setLoading(true);
        setCurrentItemId(itemId);
        draweeMetricsRef.current = ownerMetrics;
        cancelPollingRef.current?.();
        cancelPollingRef.current = null;

        getCcirDetails(itemId)
            .then(response => {
                // Enrich the drawee (current view) with metrics from the parent level
                let enriched = response;
                if (ownerMetrics) {
                    enriched = {
                        ...response,
                        coOwners: response.coOwners.map(co => {
                            if (!co.isDrawee) return co;
                            return {
                                ...co,
                                scrTotal: ownerMetrics.scrTotal,
                                scrVencido: ownerMetrics.scrVencido,
                                scrPrejuizo: ownerMetrics.scrPrejuizo,
                                pdefault: ownerMetrics.pdefault,
                                agroScore: ownerMetrics.agroScore,
                                patrimony: ownerMetrics.patrimony,
                                patrimonyDebtRatio: ownerMetrics.patrimonyDebtRatio,
                            };
                        }),
                    };
                }

                if (enriched.deepAnalysisCompleted) {
                    const coOwnersWithMetrics = enrichCoOwnersFromBureauData(enriched.coOwners, enriched.properties);
                    const metrics = calcGroupMetrics(coOwnersWithMetrics, enriched.properties);
                    setData({ ...enriched, coOwners: coOwnersWithMetrics, groupMetrics: metrics });
                } else if (enriched.deepAnalysisBatchId) {
                    setData(enriched);
                    setDeepAnalysisLoading(true);
                    cancelPollingRef.current = startPollingCcirDetails(itemId, null, {
                        onComplete: (completedData) => {
                            const withDrawee = applyDraweeMetrics(completedData, draweeMetricsRef.current);
                            setData(withDrawee);
                            setDeepAnalysisLoading(false);
                        },
                        onTimeout: (timeoutData) => {
                            setData(timeoutData);
                            setDeepAnalysisLoading(false);
                        },
                        onError: () => setDeepAnalysisLoading(false),
                    });
                } else {
                    setData(enriched);
                }
            })
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const handleNavigateToOwner = useCallback((owner: CcirOwner) => {
        if (owner.isDrawee || !data || !owner.itemId) return;
        setNavStack(prev => [...prev, { document: currentDocument, name: currentName, data }]);
        setSelectedPropertyId(null);
        setDeepAnalysisLoading(false);
        loadCcirForItem(owner.itemId, owner);
    }, [data, currentDocument, currentName, loadCcirForItem]);

    const handleNavigateBack = useCallback((targetIndex: number) => {
        setDeepAnalysisLoading(false);
        if (targetIndex < 0) {
            setNavStack([]);
            setSelectedPropertyId(null);
            setCurrentItemId(row.uuid);
            setLoading(true);
            getCcirDetails(row.uuid)
                .then(response => {
                    const enriched = enrichDraweeFromRow(response, row);
                    if (enriched.deepAnalysisCompleted) {
                        const coOwnersWithMetrics = enrichCoOwnersFromBureauData(enriched.coOwners, enriched.properties);
                        const metrics = calcGroupMetrics(coOwnersWithMetrics, enriched.properties);
                        setData({ ...enriched, coOwners: coOwnersWithMetrics, groupMetrics: metrics });
                    } else {
                        setData(enriched);
                    }
                })
                .catch(() => setData(null))
                .finally(() => setLoading(false));
            return;
        }
        const target = navStack[targetIndex];
        setNavStack(prev => prev.slice(0, targetIndex));
        setData(target.data);
        setCurrentItemId(target.data.itemId || row.uuid);
        setSelectedPropertyId(null);
    }, [navStack, row]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-[80vw] max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-600 px-6 py-4 z-10 flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Propriedades CCIR</h2>
                            <div className="relative">
                                <button
                                    onClick={() => setShowFormulas(prev => !prev)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                    title="Fórmulas de cálculo"
                                >
                                    <FaInfoCircle className="text-sm" />
                                </button>
                                {showFormulas && (
                                    <div className="absolute left-0 top-7 z-50 w-[420px] bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-500 rounded-lg shadow-lg p-4 text-xs text-gray-700 dark:text-gray-200 space-y-2">
                                        <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">Fórmulas de Cálculo</h4>
                                        <div><span className="font-medium">Participação (%):</span> Σ (área_ha × %detenção) / área_total × 100</div>
                                        <div><span className="font-medium">Patrimônio Sacado:</span> Σ (área_ha × valor_por_ha × %detenção / 100)</div>
                                        <div><span className="font-medium">Patrimônio Grupo:</span> Σ valor de todas as propriedades (área × valor/ha)</div>
                                        <div><span className="font-medium">Dívida Grupo:</span> Σ SCR Total de todos os coproprietários</div>
                                        <div><span className="font-medium">Pat/Dívida Grupo:</span> Patrimônio Grupo / Dívida Grupo</div>
                                        <div><span className="font-medium">Pat/Dívida (individual):</span> Patrimônio / SCR Total</div>
                                        <div className="pt-1 border-t border-gray-100 dark:border-slate-600 text-[10px] text-gray-400 dark:text-gray-400">
                                            Participação consolidada é ponderada pela proporção de área de cada imóvel sobre o total.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {navStack.length > 0 ? (
                            <div className="flex items-center gap-1 text-sm mt-0.5 flex-wrap">
                                <button onClick={() => handleNavigateBack(-1)} className="text-blue-600 dark:text-blue-400 hover:underline">
                                    {navStack[0].name}
                                </button>
                                {navStack.slice(1).map((entry, i) => (
                                    <span key={entry.document} className="flex items-center gap-1">
                                        <FaChevronRight className="text-[10px] text-gray-400" />
                                        <button onClick={() => handleNavigateBack(i + 1)} className="text-blue-600 dark:text-blue-400 hover:underline">
                                            {entry.name}
                                        </button>
                                    </span>
                                ))}
                                <FaChevronRight className="text-[10px] text-gray-400" />
                                <span className="text-gray-900 dark:text-gray-100 font-medium">{currentName}</span>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{row.drawee} — {row.document}</p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2">
                        <FaTimes className="text-lg" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <FaSpinner className="animate-spin text-2xl text-blue-500" />
                    </div>
                ) : data ? (
                    <div className="px-6 py-4 space-y-5">
                        {/* Summary Cards — full width top */}
                        <div className="grid grid-cols-6 gap-3">
                            <SummaryCard label="Área Total" value={`${calcTotalArea(data.properties).toLocaleString('pt-BR')} ha`} />
                            <SummaryCard label="Valor Total" value={fmtCurrency(calcTotalValue(data.properties), false)} />
                            <SummaryCard label="Part. Sacado" value={fmtParticipation(data.consolidatedParticipation)} />
                            <SummaryCard label="Patrim. Sacado" value={fmtCurrency(calcDraweePatrimony(data.properties), false)} />
                            <SummaryCard
                                label="Patrim. Grupo"
                                value={groupHasError ? 'Parcial' : (data.deepAnalysisCompleted && data.groupMetrics ? fmtCurrency(data.groupMetrics.groupPatrimony, false) : '—')}
                                muted={groupHasError || !data.deepAnalysisCompleted}
                            />
                            <SummaryCard
                                label="Pat/Dív Grupo"
                                value={groupHasError ? 'Parcial' : (data.deepAnalysisCompleted && data.groupMetrics ? `${data.groupMetrics.groupPatrimonyDebtRatio.toFixed(2)}x` : '—')}
                                muted={groupHasError || !data.deepAnalysisCompleted}
                            />
                        </div>

                        {/* Two columns: Chart (left) + Properties (right) */}
                        <div className="flex gap-5">
                            {/* Left — Chart + Legend (sticky) */}
                            <div className="w-[280px] shrink-0 self-start sticky top-[76px]">
                                <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                            {selectedPropertyId ? `Participação — ${selectedPropertyId}` : 'Participação Consolidada'}
                                        </h3>
                                        {selectedPropertyId && (
                                            <button
                                                onClick={() => setSelectedPropertyId(null)}
                                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                            >
                                                Consolidado
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-center gap-3">
                                        <PieChart owners={chartOwners} />
                                        <ChartLegend owners={chartOwners} draweeDocument={row.document} />
                                    </div>
                                </div>
                            </div>

                            {/* Right — Properties */}
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Propriedades ({data.properties.length})</h3>
                                <div className="space-y-3">
                                {Object.entries(
                                    data.properties.reduce<Record<string, CcirProperty[]>>((acc, prop) => {
                                        const key = prop.agroconsultRegion || 'Sem região';
                                        if (!acc[key]) acc[key] = [];
                                        acc[key].push(prop);
                                        return acc;
                                    }, {})
                                ).map(([region, props]) => {
                                    const regionArea = props.reduce((s, p) => s + p.area, 0);
                                    const regionValue = props.reduce((s, p) => s + p.value, 0);
                                    const cities = props.reduce<Record<string, { area: number; valorHaSum: number; value: number; count: number }>>((acc, p) => {
                                        const cityKey = `${p.city}-${p.state}`;
                                        if (!acc[cityKey]) acc[cityKey] = { area: 0, valorHaSum: 0, value: 0, count: 0 };
                                        acc[cityKey].area += p.area;
                                        acc[cityKey].valorHaSum += p.valorHa;
                                        acc[cityKey].value += p.value;
                                        acc[cityKey].count += 1;
                                        return acc;
                                    }, {});

                                    return (
                                        <div key={region} className={`border rounded-lg overflow-hidden transition-colors ${
                                            region === selectedPropertyId
                                                ? 'border-amber-400 dark:border-amber-500 ring-1 ring-amber-400 dark:ring-amber-500'
                                                : 'border-gray-200 dark:border-slate-600'
                                        }`}>
                                            <div
                                            onClick={() => setSelectedPropertyId(region === selectedPropertyId ? null : region)}
                                            className={`bg-amber-50 dark:bg-amber-900/20 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors ${
                                                region === selectedPropertyId ? 'bg-amber-100 dark:bg-amber-900/30' : ''
                                            }`}
                                        >
                                            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">{region}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {regionArea.toLocaleString('pt-BR')} ha • {fmtCurrency(regionValue, false)} • {props.length} prop.
                                            </span>
                                        </div>
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 dark:bg-slate-700">
                                                    <tr>
                                                        <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Município/UF</th>
                                                        <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Área (ha)</th>
                                                        <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">R$/ha</th>
                                                        <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Valor Total</th>
                                                        <th className="text-center px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Imóveis</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-slate-600">
                                                    {Object.entries(cities).map(([city, info]) => (
                                                        <tr
                                                            key={city}
                                                            onClick={() => setSelectedPropertyId(city === selectedPropertyId ? null : city)}
                                                            className={`cursor-pointer transition-colors ${
                                                                city === selectedPropertyId
                                                                    ? 'bg-amber-100 dark:bg-amber-900/30'
                                                                    : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                                                            }`}
                                                            style={city === selectedPropertyId ? { boxShadow: 'inset 3px 0 0 0 #f59e0b' } : undefined}
                                                        >
                                                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100 font-medium">{city}</td>
                                                            <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{info.area.toLocaleString('pt-BR')}</td>
                                                            <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{fmtCurrency(info.valorHaSum / info.count, false)}</td>
                                                            <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{fmtCurrency(info.value, false)}</td>
                                                            <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{info.count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        </div>

                        {/* Co-owners Table */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Coproprietários</h3>

                            {/* Bureau Error Banner — only real technical failures (ERROR), not "no record" (NOT_FOUND) */}
                            {data.deepAnalysisCompleted && data.deepAnalysisBatchId && (
                                <BureauErrorBanner
                                    failedBureaus={getFailedBureaus(data.coOwners)}
                                    retrying={deepAnalysisLoading}
                                    onRetry={() => handleRetryFailedBureaus(data.deepAnalysisBatchId!, getFailedBureaus(data.coOwners))}
                                />
                            )}

                            <div className="border border-gray-200 dark:border-slate-600 rounded-lg overflow-hidden overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-slate-700">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">CPF</th>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Nome</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">% Part.</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">SCR Total</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">SCR Vencido</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">SCR Prejuízo</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">PDefault</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">AgroScore</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Patrimônio</th>
                                            <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Pat/Dívida</th>
                                            <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Consultas</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-slate-600">
                                        {data.coOwners.map(owner => {
                                            const ownerStatus = getOwnerBureauStatus(owner);
                                            const errorDetails = getOwnerErrorDetails(owner);
                                            const rowHighlight = ownerStatus === OwnerBureauStatus.ERROR
                                                ? 'bg-red-50 dark:bg-red-900/20'
                                                : ownerStatus === OwnerBureauStatus.NOT_FOUND
                                                    ? 'bg-amber-50/60 dark:bg-amber-900/10'
                                                    : owner.isDrawee ? 'bg-blue-50/50 dark:bg-blue-900/10' : '';
                                            return (
                                            <tr
                                                key={owner.document}
                                                onClick={data.deepAnalysisCompleted && !owner.isDrawee && owner.itemId ? () => handleNavigateToOwner(owner) : undefined}
                                                className={`${rowHighlight} ${
                                                    data.deepAnalysisCompleted && !owner.isDrawee && owner.itemId ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700' : ''
                                                }`}
                                            >
                                                <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">{maskDocument(owner.document)}</td>
                                                <td className="px-3 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                                    <span className={data.deepAnalysisCompleted && !owner.isDrawee && owner.itemId ? 'text-blue-600 dark:text-blue-400 hover:underline' : ''}>
                                                        {owner.name}
                                                    </span>
                                                    {unmaskDocument(owner.document) === unmaskDocument(row.document) && <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">(sacado)</span>}
                                                    {data.deepAnalysisCompleted && !owner.isDrawee && owner.itemId && (
                                                        <FaSearchPlus className="inline ml-1 text-xs text-gray-400" />
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{fmtParticipation(owner.participationPercent)}</td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.scrTotal != null ? fmtCurrency(owner.scrTotal, false) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.scrVencido != null ? (
                                                        <span className={owner.scrVencido > 0 ? 'text-red-600 dark:text-red-400' : ''}>
                                                            {fmtCurrency(owner.scrVencido, false)}
                                                        </span>
                                                    ) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.scrPrejuizo != null ? (
                                                        <span className={owner.scrPrejuizo > 0 ? 'text-red-600 dark:text-red-400' : ''}>
                                                            {fmtCurrency(owner.scrPrejuizo, false)}
                                                        </span>
                                                    ) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.pdefault != null ? `${owner.pdefault.toFixed(2)}%` : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.agroScore != null ? owner.agroScore : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.patrimony != null ? fmtCurrency(owner.patrimony, false) : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                                    {owner.patrimonyDebtRatio != null ? `${owner.patrimonyDebtRatio.toFixed(2)}x` : <span className="text-gray-400">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-left">
                                                    {ownerStatus === OwnerBureauStatus.OK ? (
                                                        <span className="text-gray-400">—</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setErrorDetailOwner(owner); }}
                                                            className="flex flex-wrap gap-1"
                                                            title="Ver detalhes da consulta"
                                                        >
                                                            {errorDetails.map((d, i) => (
                                                                <span
                                                                    key={`${d.bureau}-${i}`}
                                                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                                                                        d.cause === 'ERROR'
                                                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                                    }`}
                                                                >
                                                                    {d.cause === 'ERROR'
                                                                        ? <FaExclamationTriangle className="shrink-0 w-2.5 h-2.5" />
                                                                        : <FaInfoCircle className="shrink-0 w-2.5 h-2.5" />}
                                                                    {d.bureau}
                                                                </span>
                                                            ))}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Group Metrics (after deep analysis) — hidden when data is partial (real errors) */}
                        {data.deepAnalysisCompleted && !groupHasError && data.groupMetrics && (
                            <div className="border-2 border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50/50 dark:bg-green-900/10">
                                <h3 className="text-sm font-medium text-green-800 dark:text-green-300 mb-3">Análise do Grupo</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Patrimônio Grupo</div>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{fmtCurrency(data.groupMetrics.groupPatrimony, false)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Dívida Grupo</div>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{fmtCurrency(data.groupMetrics.groupDebt, false)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Pat/Dívida Grupo</div>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{data.groupMetrics.groupPatrimonyDebtRatio.toFixed(2)}x</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200 dark:border-slate-600">
                            {!data.deepAnalysisCompleted && (
                                <button
                                    onClick={handleDeepAnalysis}
                                    disabled={deepAnalysisLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-400 disabled:opacity-50 transition-colors"
                                >
                                    {deepAnalysisLoading ? <FaSpinner className="animate-spin" /> : <FaSearchPlus />}
                                    {deepAnalysisLoading ? 'Consultando SCR...' : 'Análise Aprofundada'}
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-16 text-gray-400 dark:text-gray-500">Sem dados CCIR disponíveis</div>
                )}
            </div>

            <BureauErrorDetailModal owner={errorDetailOwner} onClose={() => setErrorDetailOwner(null)} />
        </div>
    );
}
